import { tableStore } from '../stores/table-store';
import { isGroupRow } from '../stores/utils/group-row-guards';
import {
  checkSolutionHealth,
  type HealthIssue,
} from '../stores/utils/solution-health';
import { isServiceScriptFile, transformActionFileData } from '../utils';
import { useProStudioCtxSelector } from '../xstate/app.xstate';

/**
 * The last check, and what it was run on. The Build bar's badge, the header's
 * Publish and the Health panel all ask on every render — and those render
 * often (scrolling the table re-renders the header) — so the check runs only
 * when its inputs change. `getAllSortedRows()` hands back the same array
 * until the rows change, so identity is enough to tell.
 */
let cache: {
  rows: unknown;
  commands: unknown;
  scripts: string;
  issues: HealthIssue[];
} | null = null;

/**
 * The solution's health, from the rule-based checks in
 * `stores/utils/solution-health.ts`. Read from an observer so it re-runs as
 * rows change.
 */
export const useSolutionHealth = (): HealthIssue[] => {
  const scripts = useProStudioCtxSelector(
    (ctx) =>
      (ctx.draft?.files ?? [])
        .filter(isServiceScriptFile)
        .map((f) => transformActionFileData(f).name),
    { isEqualityDeep: true }
  );
  const allRows = tableStore.getAllSortedRows();
  const commands = tableStore.actionNodeNames;
  const scriptsKey = scripts.join('\n');
  if (
    cache &&
    cache.rows === allRows &&
    cache.commands === commands &&
    cache.scripts === scriptsKey
  ) {
    return cache.issues;
  }
  const issues = checkSolutionHealth(
    allRows.filter((r) => !isGroupRow(r)),
    { nativeCommands: commands, uploadedScripts: scripts }
  );
  cache = { rows: allRows, commands, scripts: scriptsKey, issues };
  return issues;
};
